import { Controller } from "@hotwired/stimulus";
import { PDFDocument } from "pdf-lib";

const formatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export default class extends Controller {
  handlePdfButtonClick = (e) => {
    e.preventDefault();
    const id = e.target.id;
    const pdf_type = e.target.getAttribute("data-pdf-type");

    fetch(`/zeus/merchant_applications/${id}.json`)
      .then((response) => response.json())
      .then((data) => {
        this.generatePdf(pdf_type, data);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  generatePdf = async (pdf_type, data) => {
    const formUrl = `/${pdf_type}-application.pdf`;
    const formPdfBytes = await fetch(formUrl).then((res) => res.arrayBuffer());
    const pdfDoc = await PDFDocument.load(formPdfBytes);
    const form = pdfDoc.getForm();

    this.addBusinessInfoFields(data, form);
    this.addBusinessOwnerFields(data, form);
    this.addBankAccountFields(data, form);
    if (pdf_type === "vci") {
      this.addAchFeeFields(data, form);
      this.addAchTransProfileFields(data, form);
    } else if (pdf_type === "nuvei" || pdf_type === "mes") {
      this.addCardFeeFields(data, form);
      this.addCardTransProfileFields(data, form);
    }

    this.addTransactionMetrics(data, form);

    const pdfBytes = await pdfDoc.save();
    this.downloadPdf(pdfBytes, pdf_type);
  };

  downloadPdf = (pdfBytes, pdf_type) => {
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `${pdf_type}-completed-application.pdf`;
    link.click();

    URL.revokeObjectURL(url);
  };

  addTextField = (form, field_name, field_value) => {
    const fields = form.getFields().map((f) => f.getName());
    if (fields.includes(field_name)) {
      if (field_value != null) {
        typeof field_value !== "string" ? (field_value = field_value.toString()) : null;
      }
      form.getTextField(field_name).setText(field_value || "");
    }
  };

  addCheckMark = (form, checkbox_name) => {
    const fields = form.getFields().map((f) => f.getName());
    if (fields.includes(checkbox_name)) {
      form.getCheckBox(checkbox_name).check();
    }
  };

  normalize_cents = (cents) => {
    return (cents / 100).toString();
  };

  addBusinessInfoFields = (data, form) => {
    const business_info = data.business_information;
    const business_info_fields = {
      "Legal Name": "legal_name",
      "IRS Filing Name": "legal_name",
      EIN: "ein",
      "Legal Address": "legal_address",
      Phone: "customer_service_phone",
      URL: "website",
      "Business Legal Name": "legal_name",
      "Business Legal Address": "legal_address",
      "Business Legal Phone": "customer_service_phone",
      "Business Description": "description",
      Email: "email",
      "Date Established": "date_established",
      "Mailing Address": "mailing_address",
      DBA: "dba",
      "DBA Address": "dba_address",
      "DBA Phone": "customer_service_phone",
      "Legal Entity": "legal_entity_type",
      "Contact Name": "contact_name",
      "Contact Title": "contact_title",
      "Contact Phone": "contact_phone",
      "Contact Email": "email",
      Industry: "industry",
      "Customer Service Phone": "customer_service_phone",
      "Legal Entity Type": "legal_entity_type",
      "Formation State": "state_of_formation",
      "Business Location": "business_location",
    };

    Object.keys(business_info_fields).forEach((field) => {
      this.addTextField(form, field, business_info?.[business_info_fields[field]]);
    });

    const entity_types = {
      "Sole Proprietorship": "Entity Type Sole Proprietor",
      Partnership: "Entity Type Partnership",
      "Limited Liability Company": "Entity Type Limited Liability",
      Corporation: "Entity Type Corporation",
      Government: "Entity Type Government",
      Nonprofit: "Entity Type Non-profit",
      "Pubicly Traded": "Entity Type Publicly Traded",
      Other: "Entity Type Other",
    };

    const selected_entity_type = data.business_information?.legal_entity_type;
    this.addCheckMark(form, entity_types[selected_entity_type]);
  };

  addBusinessOwnerFields = (data, form) => {
    data.business_owners.forEach((owner, i) => {
      const ownerNum = `${i + 1}`;
      if (ownerNum === "1") {
        this.addTextField(form, `Owner 1 Name p1`, owner?.legal_name);
        this.addTextField(form, `Owner 1 Title p1`, owner?.title);
      }
      this.addTextField(form, `Owner ${ownerNum} Name`, owner?.legal_name);
      this.addTextField(form, `Owner ${ownerNum} Title`, owner?.title);
      this.addTextField(form, `Owner ${ownerNum} Name Signature`, owner?.legal_name);
      this.addTextField(form, `Owner ${ownerNum} Percentage`, owner.percent_ownership?.toString());
      this.addTextField(form, `Owner ${ownerNum} SSN`, owner?.ssn);
      this.addTextField(form, `Owner ${ownerNum} DOB`, owner?.birthdate);
      this.addTextField(form, `Owner ${ownerNum} Address`, owner?.address);
      this.addTextField(form, `Owner ${ownerNum} Phone`, owner?.phone);
      this.addTextField(form, `Owner ${ownerNum} Email`, owner?.email_address);

      this.addTextField(form, `Beneficial Ownership Name ${ownerNum}`, owner?.legal_name);
      this.addTextField(form, `Beneficial Ownership Title ${ownerNum}`, owner?.title);

      this.addTextField(form, `Owner ${ownerNum} Drivers License`, owner?.drivers_license);
      this.addTextField(
        form,
        `Owner ${ownerNum} Drivers License State`,
        owner?.drivers_license_state,
      );
      this.addTextField(
        form,
        `Owner ${ownerNum} Drivers License Issuance Date`,
        owner?.drivers_license_issuance_date,
      );
      this.addTextField(
        form,
        `Owner ${ownerNum} Drivers License Expiration`,
        owner?.drivers_license_expiration_date,
      );

      const ssn_checkbox =
        owner.ssn != null
          ? `Owner ${ownerNum} SSN Yes Checkbox`
          : `Owner ${ownerNum} SSN No Checkbox`;
      this.addCheckMark(form, ssn_checkbox);

      const control_checkbox =
        ownerNum === "1"
          ? `Owner ${ownerNum} Control Yes Checkbox`
          : `Owner ${ownerNum} Control No Checkbox`;
      this.addCheckMark(form, control_checkbox);
    });
  };

  addBankAccountFields = (data, form) => {
    const deposit_account_fields = {
      "Bank Account Owner": "name_on_account",
      "Bank Name": "bank_name",
      "Deposit Routing": "routing_number",
      "Deposit Account": "account_number",
    };

    const fee_account_fields = { "Fee Routing": "routing_number", "Fee Account": "account_number" };

    data.bank_accounts.forEach((account) => {
      if (account.purpose === "deposit") {
        Object.keys(deposit_account_fields).forEach((field) => {
          this.addTextField(form, field, account?.[deposit_account_fields[field]]);
        });

        if (account?.account_type === "checking") {
          this.addCheckMark(form, "Bank Account Type Checking");
        } else if (account?.account_type === "savings") {
          this.addCheckMark(form, "Bank Account Type Savings");
        }
      } else if (account.purpose === "fee") {
        Object.keys(fee_account_fields).forEach((field) => {
          this.addTextField(form, field, account?.[fee_account_fields[field]]);
        });
      }
    });
  };

  addAchFeeFields = (data, form) => {
    const ach_fee = data.ach_fee_schedule;

    const ach_fee_fields = {
      "Account Validation Fee": this.normalize_cents(ach_fee?.account_validation_cents),
      "Annual Fee": this.normalize_cents(ach_fee?.annual_fee_cents),
      "Application Fee": this.normalize_cents(ach_fee?.application_fee_cents),
      "Discount Rate": ach_fee?.discount_rate,
      "Monthly Minimum": this.normalize_cents(ach_fee?.monthly_minimum_cents),
      "Monthly Service Fee": this.normalize_cents(ach_fee?.monthly_service_fee_cents),
      "Per Item": this.normalize_cents(ach_fee?.per_item_cents),
      "Regular Return Fee": this.normalize_cents(ach_fee?.regular_return_fee_cents),
      "Settlement Fee": this.normalize_cents(ach_fee?.settlement_fee_cents),
      "Tier Three Discount Rate": ach_fee?.tier_three_discount_rate,
      "Tier Three Per Item Cents": this.normalize_cents(ach_fee?.tier_three_per_item_cents),
      "Tier Two Discount Rate": ach_fee?.tier_two_discount_rate,
      "Tier Two Per Item": this.normalize_cents(ach_fee?.tier_two_per_item_cents),
      "Unauthorized Return Fee": this.normalize_cents(ach_fee?.unauthorized_return_fee_cents),
    };

    Object.keys(ach_fee_fields).forEach((field) => {
      this.addTextField(form, field, ach_fee_fields[field]);
    });
  };

  addCardFeeFields = (data, form) => {
    const card_fee = data.card_fee_schedule;

    const card_fee_fields = {
      "Amex Pass Through Per Item": this.normalize_cents(
        card_fee?.amex_pass_through_per_item_cents,
      ),
      "Amex Pass Through Rate": card_fee?.amex_pass_through_rate,
      "AMEX Cost Plus": card_fee?.amex_cost_plus_bp / 100,
      "Auth Fee": this.normalize_cents(card_fee?.auth_fee),
      "Avs Fee": this.normalize_cents(card_fee?.avs_fee_cents),
      Batch: this.normalize_cents(card_fee?.batch_cents),
      Chargeback: this.normalize_cents(card_fee?.chargeback_cents),
      "Credit Qualified Per Item": this.normalize_cents(card_fee?.credit_qualified_per_item_cents),
      "Credit Qualified Rate": card_fee?.credit_qualified_rate,
      "Debit Qualified Per Item": this.normalize_cents(card_fee?.debit_qualified_per_item_cents),
      "Debit Qualified Rate": card_fee?.debit_qualified_rate,
      "Mid Qualified Per Item": this.normalize_cents(card_fee?.mid_qualified_per_item_cents),
      "Mid Qualified Rate": card_fee?.mid_qualified_rate,
      "Minimum Discount": this.normalize_cents(card_fee?.minimum_discount_cents),
      "Non Qualified Per Item": this.normalize_cents(card_fee?.non_qualified_per_item_cents),
      "Non Qualified Rate": card_fee?.non_qualified_rate,
      "Per Item": this.normalize_cents(card_fee?.per_item_cents),
      Rate: card_fee?.non_qualified_rate,
    };

    Object.keys(card_fee_fields).forEach((field) => {
      this.addTextField(form, field, card_fee_fields[field]);
    });
  };

  addCardTransProfileFields = (data, form) => {
    const card_transaction = data.card_transaction_profile;
    const percent_card_no_amex = (100 - card_transaction?.yearly_percent_amex) / 100;
    const card_transaction_fields = {
      "Average Ticket": formatter.format(card_transaction?.average_ticket),
      "Average Ticket AMEX": card_transaction?.average_ticket,
      "Highest Monthly Volume": formatter.format(card_transaction?.highest_monthly_volume),
      "Yearly Percent Amex": card_transaction?.yearly_percent_amex,
      "Yearly Volume": formatter.format(card_transaction?.yearly_volume),
      "High Ticket": formatter.format(card_transaction?.high_ticket),
      "Monthly Volume": formatter.format(
        (card_transaction?.yearly_volume * percent_card_no_amex) / 12,
      ),
      "Monthly Volume AMEX": formatter.format(
        (card_transaction?.yearly_volume * card_transaction?.yearly_percent_amex) / 100 / 12,
      ),
      "Yearly Volume VISA": formatter.format(
        card_transaction?.yearly_volume * percent_card_no_amex,
      ),
      "Yearly Volume Amex": formatter.format(
        card_transaction?.yearly_volume * (card_transaction?.yearly_percent_amex / 100),
      ),
    };

    Object.keys(card_transaction_fields).forEach((field) => {
      this.addTextField(form, field, card_transaction_fields[field]);
    });

    const refund_policies = {
      "No Refunds or Exchanges": "Refund No Refund",
      "Exchanges Only": "Refund Exchanges Only",
      "Refunds in 30 days or less": "Refund 30 Days",
      "Refund Credit": "Refund Credit",
      "Refund Merchandise": "Refund Merchandise",
    };
    const selected_refund_policy = data.card_transaction_profile?.refund_policy;
    this.addCheckMark(form, refund_policies[selected_refund_policy]);

    const delivery_days = {
      Immediate: "Delivery Days Immediate",
      "1 to 7": "Delivery Days 1 7",
      "8 to 14": "Delivery Days 8 14",
      "15 to 30": "Delivery Days 15 30",
      "Over 30": "Delivery Days Over 30",
    };
    const selected_delivery = data.card_transaction_profile?.deliver_days_range;
    this.addCheckMark(form, delivery_days[selected_delivery]);
  };

  addTransactionMetrics = (data, form) => {
    const card_transaction = data.card_transaction_profile;
    const ach_transaction = data.ach_transaction_profile;

    let transaction_fields = {};
    if (card_transaction != null) {
      transaction_fields = {
        "Acceptance Internet": card_transaction?.acceptance_internet_percent,
        "Acceptance Manual": card_transaction?.acceptance_manual_percent,
        "Acceptance Tele": card_transaction?.acceptance_tele_percent,
        "Acceptance Swipe": card_transaction?.acceptance_swipe_percent,
        "Acceptance Manual Tele":
          card_transaction?.acceptance_tele_percent + card_transaction?.acceptance_manual_percent,
        "Customer Type B2B": card_transaction?.customer_type_business_percent,
        "Customer Type B2C": 100 - card_transaction?.customer_type_business_percent,
      };
    } else if (ach_transaction != null) {
      transaction_fields = {
        "Acceptance Internet": ach_transaction?.acceptance_internet_percent,
        "Acceptance Manual": ach_transaction?.acceptance_manual_percent,
        "Acceptance Tele": ach_transaction?.acceptance_tele_percent,
        "Acceptance Swipe": ach_transaction?.acceptance_swipe_percent,
        "Customer Type B2B": ach_transaction?.customer_type_business_percent,
        "Customer Type B2C": 100 - ach_transaction?.customer_type_business_percent,
      };
    }

    Object.keys(transaction_fields).forEach((field) => {
      this.addTextField(form, field, transaction_fields[field]);
    });
  };

  addAchTransProfileFields = (data, form) => {
    const ach_transaction = data.ach_transaction_profile;

    const ach_transaction_fields = {
      "Average Ticket": formatter.format(ach_transaction?.average_ticket),
      "High Ticket": formatter.format(ach_transaction?.high_ticket),
      "Monthly Count": ach_transaction?.monthly_transaction_count,
      "Monthly Amount": formatter.format(ach_transaction?.yearly_volume / 12),
    };

    Object.keys(ach_transaction_fields).forEach((field) => {
      this.addTextField(form, field, ach_transaction_fields[field]);
    });
  };
}
